import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  Signal,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ButtonComponent,
  ContextMenuComponent,
  ContextMenuItem,
  FaIconComponent,
  NotificationComponent,
  Severity,
} from '@akostm/orbit-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  faCircleExclamation,
  faEllipsis,
  faEllipsisVertical,
  faLink,
  faPencil,
} from '@fortawesome/free-solid-svg-icons';
import { MessageStore, ParticipantWithSourceId, UserState } from '../../../stores';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ParticipantKind, UserRole } from '@clientwire/api';

interface MenuClickValue {
  type: string;
  participant?: Signal<ParticipantWithSourceId>;
}

interface ParticipantNotificationStatus {
  status: 'REQUESTED' | 'SENT' | 'FAILED' | 'DELIVERED';
  createdAt?: Date | null;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[shared-participant-list-item]',
  standalone: true,
  imports: [CommonModule, NotificationComponent, FaIconComponent, TranslateModule, ButtonComponent],
  templateUrl: './participant-list-item.component.html',
  styleUrl: './participant-list-item.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantListItemComponent implements OnDestroy {
  user = input<UserState>();
  participant = input.required<ParticipantWithSourceId>();
  menuClicked = output<MenuClickValue>();
  readonly #now = signal<Date>(new Date());
  conversationIsOpen = computed(
    () => this.#now().getTime() - (this.participant().hadConversationOpenAt?.getTime() ?? 0) < 10000
  );
  messages = computed(() => this.#messageStore.entities()[0]?.messages ?? []);

  isTyping = computed(() => {
    // Don't show typing indicator for the current user
    if (this.participant()?.userId === this.user()?.id) {
      return false;
    }

    if (this.participant().sourceId) {
      const lastMessage = this.messages()[0];
      return (
        this.#now().getTime() - (this.participant().wasTypingAt?.getTime() ?? 0) < 5000 &&
        lastMessage?.sourceId !== this.participant().sourceId
      );
    }
    return this.#now().getTime() - (this.participant().wasTypingAt?.getTime() ?? 0) < 5000;
  });

  #dateUpdaterTimeout: number | null = null;
  #notificationStatusOverlayRef: import('@angular/cdk/overlay').OverlayRef | null = null;
  readonly SeverityEnum = Severity;
  readonly typingIcon = faEllipsis;
  readonly menuIcon = faEllipsisVertical;
  readonly errorIcon = faCircleExclamation;
  readonly #messageStore = inject(MessageStore);
  readonly #overlay = inject(Overlay);
  readonly #translateService = inject(TranslateService);
  readonly editIcon = faPencil;
  readonly linkIcon = faLink;
  readonly UserRole = UserRole;
  readonly ParticipantKind = ParticipantKind;

  hasFailedNotification = computed(() => {
    const notificationStatus = this.participant().notificationStatus as
      | { [key: string]: ParticipantNotificationStatus }
      | null
      | undefined;
    if (!notificationStatus) return false;
    return Object.values(notificationStatus).some((status) => status.status === 'FAILED');
  });

  getFailedNotificationTooltip = computed(() => {
    const notificationStatus = this.participant().notificationStatus as
      | { [key: string]: ParticipantNotificationStatus }
      | null
      | undefined;
    if (!notificationStatus) return '';

    const failedChannels: string[] = [];
    Object.entries(notificationStatus).forEach(([channel, status]) => {
      if (status.status === 'FAILED') {
        failedChannels.push(channel);
      }
    });

    if (failedChannels.length === 0) return '';

    return this.#translateService.instant(
      'SHARED.PARTICIPANT_LIST.NOTIFICATION_FAILED_TOOLTIP.TITLE',
      { channels: failedChannels.join(', ') }
    );
  });

  readonly FULL_MENU: ContextMenuItem[] = [
    {
      label: 'SHARED.PARTICIPANT_LIST.MENU.EDIT',
      icon: this.editIcon,
      clickValue: {
        type: 'edit',
        participant: this.participant,
      },
    },
    {
      label: 'SHARED.PARTICIPANT_LIST.MENU.LINK',
      icon: this.linkIcon,
      clickValue: {
        type: 'link',
        participant: this.participant,
      },
    },
  ];

  constructor() {
    this.#updateDateSignalToCheck();
  }

  ngOnDestroy(): void {
    if (this.#dateUpdaterTimeout !== null) {
      clearTimeout(this.#dateUpdaterTimeout);
    }
    if (this.#notificationStatusOverlayRef) {
      this.#notificationStatusOverlayRef.detach();
      this.#notificationStatusOverlayRef = null;
    }
  }

  openNotificationStatus(event: Event) {
    event.stopPropagation();

    // If overlay is already open, don't do anything
    if (this.#notificationStatusOverlayRef) {
      return;
    }

    const positionStrategy = this.#overlay
      .position()
      .flexibleConnectedTo(event.currentTarget as HTMLElement)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 5,
        } as ConnectedPosition,
      ])
      .withPush(false);
    const scrollStrategy = this.#overlay.scrollStrategies.close();
    this.#notificationStatusOverlayRef = this.#overlay.create({
      positionStrategy: positionStrategy,
      scrollStrategy: scrollStrategy,
      panelClass: 'notification-status-overlay',
    });

    const menuItems: ContextMenuItem[] = [
      {
        label: this.getFailedNotificationTooltip(),
      },
    ];

    const more = new ComponentPortal(ContextMenuComponent);
    const ref = this.#notificationStatusOverlayRef.attach(more);
    ref.setInput('items', menuItems);

    this.#notificationStatusOverlayRef._outsidePointerEvents.subscribe(() => {
      this.#notificationStatusOverlayRef?.detach();
      this.#notificationStatusOverlayRef = null;
    });
  }

  closeNotificationStatus() {
    if (this.#notificationStatusOverlayRef) {
      this.#notificationStatusOverlayRef.detach();
      this.#notificationStatusOverlayRef = null;
    }
  }

  openMenu(event: Event) {
    const positionStrategy = this.#overlay
      .position()
      .flexibleConnectedTo(event.target as HTMLElement)
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
          offsetX: 15,
        } as ConnectedPosition,
      ])
      .withPush(false);
    const scrollStrategy = this.#overlay.scrollStrategies.close();
    const overlayRef = this.#overlay.create({
      positionStrategy: positionStrategy,
      scrollStrategy: scrollStrategy,
    });

    let menuItems: ContextMenuItem[] = [...this.FULL_MENU];
    if (this.user()?.role !== UserRole.Owner) {
      if (
        this.user()?.role === UserRole.User &&
        this.participant()?.kind === UserRole.User &&
        this.participant()?.userId !== this.user()?.id
      ) {
        menuItems = this.#removeEditMenuItem(menuItems);
      }
    }

    if (this.participant()?.kind !== ParticipantKind.Client) {
      menuItems = this.#removeLinkMenuItem(menuItems);
    }

    if (menuItems.length === 0) {
      menuItems.push({
        label: 'SHARED.PARTICIPANT_LIST.MENU.NO_ITEMS',
      });
    }

    const more = new ComponentPortal(ContextMenuComponent);
    const ref = overlayRef.attach(more);
    ref.setInput('items', menuItems);
    ref.instance.itemClicked.subscribe((value) => {
      if (value && (value as MenuClickValue)?.type) {
        this.menuClicked.emit(value as MenuClickValue);
      }
      overlayRef.detach();
    });
    overlayRef._outsidePointerEvents.subscribe(() => {
      overlayRef.detach();
    });
  }

  #updateDateSignalToCheck() {
    this.#now.set(new Date());
    if (this.#dateUpdaterTimeout !== null) {
      clearTimeout(this.#dateUpdaterTimeout);
    }
    this.#dateUpdaterTimeout = window.setTimeout(() => {
      this.#updateDateSignalToCheck();
    }, 1000);
  }

  #removeEditMenuItem(menuItems: ContextMenuItem[]) {
    return menuItems.filter(
      (item) => !(item.clickValue && (item.clickValue as MenuClickValue)?.type === 'edit')
    );
  }

  #removeLinkMenuItem(menuItems: ContextMenuItem[]) {
    return menuItems.filter(
      (item) => !(item.clickValue && (item.clickValue as MenuClickValue)?.type === 'link')
    );
  }
}
